import React, {useCallback, useMemo, useState} from 'react'
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, TextField, Grid, MenuItem, FormControl, InputLabel} from '@mui/material'
import {useAppDispatch} from '../../../redux/storeExports'
import { resendInviteWithMuid } from '../../../redux/corporateService'
import Select, { SelectChangeEvent } from "@mui/material/Select"

enum GrantType {
  CORPORATE = "corporate_grant",
  FAMILY = "family_grant",
}

interface FormState {
  muid: string,
  grant_type: GrantType,
}

const INITIAL_FORM_STATE: FormState = {
  muid: '',
  grant_type: GrantType.CORPORATE
}

const RetriggerScansPopup: React.FC = () => {
  const dispatch = useAppDispatch()

  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [dialogOpen, setDialogOpen] = useState(false)

  const resetForm = useCallback(() => {
    setFormState(INITIAL_FORM_STATE)
  }, [])

  const handleMuidChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    setFormState((prev) => ({...prev, muid: value}))
  }, [])

  const handleGrantChange = useCallback(
    (event: SelectChangeEvent<GrantType>) => {
      const value = event.target.value as GrantType
      setFormState((prev) => ({ ...prev, grant_type: value }))
    },
    []
  )

  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    resetForm()
    setDialogOpen(false)
  }, [resetForm])

  const handleRetriggerScans = useCallback(async () => {
    const { muid, grant_type} = formState

    if (!muid) return

    try {
      await dispatch(resendInviteWithMuid({ muid: muid, grant_type: grant_type })).unwrap()
      handleDialogClose()
    } catch (error) {
      console.error('Failed to retrigger invite:', error)
    }
  }, [dispatch, formState, handleDialogClose])

  const isSubmitDisabled = useMemo(() => !formState.muid, [formState.muid])

  return (
    <div>
      <Button
        variant="contained"
        color="warning"
        onClick={handleDialogOpen}
        sx={{minWidth: '150px'}}
      >
        Retrigger Invite Email
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <DialogContentText>
            Are you sure you want to retrigger invite email for the provided MUID?
          </DialogContentText>
          <Grid container spacing={2} sx={{ my: 3 }}>
            <Grid item xs={6}>
              <TextField
                value={formState.muid}
                onChange={handleMuidChange}
                label="MUID"
                size="small"
                fullWidth
                  />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="grant-type-label">Grant Type</InputLabel>
                <Select
                  labelId="grant-type-label"
                  id="grant-type"
                  value={formState.grant_type}
                  onChange={handleGrantChange}
                  label="Grant Type"
                >
                  <MenuItem value={GrantType.FAMILY}>Family Grant</MenuItem>
                  <MenuItem value={GrantType.CORPORATE}>Corporate Grant</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleRetriggerScans}
            color="primary"
            disabled={isSubmitDisabled}
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default RetriggerScansPopup